import { syntaxTree } from "@codemirror/language";
import { Prec, RangeSetBuilder, type Extension } from "@codemirror/state";
import {
  Decoration,
  EditorView,
  highlightTrailingWhitespace,
  highlightWhitespace,
  keymap,
  MatchDecorator,
  ViewPlugin,
  WidgetType,
  type DecorationSet,
  type ViewUpdate,
} from "@codemirror/view";

/**
 * Extensões de editor dos plugins oficiais. Todas são só visuais (decorações), exceto o
 * Prettier, que edita o arquivo quando a pessoa pede — exatamente o que cada manifesto declara.
 */

// ───────────── Guias de indentação ─────────────
// Uma decoração por linha com o nível de indentação; o desenho é um degradê repetido em CSS
// (barato: nenhum elemento extra no DOM).
const INDENT_UNIT = 2;

function indentDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  const tabSize = view.state.tabSize;
  for (const { from, to } of view.visibleRanges) {
    for (let pos = from; pos <= to;) {
      const line = view.state.doc.lineAt(pos);
      let cols = 0;
      for (const ch of line.text) {
        if (ch === " ") cols += 1;
        else if (ch === "\t") cols += tabSize;
        else break;
      }
      const levels = Math.floor(cols / INDENT_UNIT);
      if (levels > 0) {
        builder.add(
          line.from,
          line.from,
          Decoration.line({
            attributes: { class: "cm-indent-guides", style: `--indent:${levels * INDENT_UNIT}` },
          }),
        );
      }
      pos = line.to + 1;
    }
  }
  return builder.finish();
}

export const indentGuides: Extension = [
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = indentDecorations(view);
      }
      update(u: ViewUpdate) {
        if (u.docChanged || u.viewportChanged) this.decorations = indentDecorations(u.view);
      }
    },
    { decorations: (v) => v.decorations },
  ),
  EditorView.baseTheme({
    ".cm-indent-guides": {
      backgroundImage: `repeating-linear-gradient(to right, var(--indent-guide, rgba(128,128,128,.22)) 0 1px, transparent 1px ${INDENT_UNIT}ch)`,
      backgroundSize: "calc(var(--indent) * 1ch) 100%",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "6px 0",
    },
  }),
];

// ───────────── Parênteses arco-íris ─────────────
// Profundidade pela árvore sintática: parênteses dentro de strings e comentários não contam.
const RAINBOW = ["#f5c451", "#c792ea", "#5fb3f6", "#4fd6be", "#ff9e64", "#f07178"];
const OPEN = new Set(["(", "[", "{"]);
const CLOSE = new Set([")", "]", "}"]);
// Classes com !important: o realce de sintaxe do tema fica num span interno e venceria a cor.
const rainbowMarks = RAINBOW.map((_, i) => Decoration.mark({ class: `cm-rainbow-${i}` }));
const rainbowTheme = EditorView.baseTheme(
  Object.fromEntries(
    RAINBOW.map((color, i) => [`.cm-rainbow-${i}, .cm-rainbow-${i} *`, { color: `${color} !important` }]),
  ),
);

function rainbowDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  const { from, to } = view.viewport;
  let depth = 0;
  syntaxTree(view.state).iterate({
    to,
    enter: (node) => {
      if (OPEN.has(node.name)) {
        if (node.from >= from) builder.add(node.from, node.to, rainbowMarks[depth % RAINBOW.length]!);
        depth++;
      } else if (CLOSE.has(node.name)) {
        depth = Math.max(0, depth - 1);
        if (node.from >= from) builder.add(node.from, node.to, rainbowMarks[depth % RAINBOW.length]!);
      }
    },
  });
  return builder.finish();
}

export const rainbowBrackets: Extension = [
  rainbowTheme,
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = rainbowDecorations(view);
      }
      update(u: ViewUpdate) {
        if (u.docChanged || u.viewportChanged || syntaxTree(u.startState) !== syntaxTree(u.state)) {
          this.decorations = rainbowDecorations(u.view);
        }
      }
    },
    { decorations: (v) => v.decorations },
  ),
];

// ───────────── TODOs em destaque ─────────────
export const TODO_RE = /\b(TODO|FIXME|HACK|XXX)\b:?/g;
const todoMatcher = new MatchDecorator({
  regexp: TODO_RE,
  decoration: Decoration.mark({ class: "cm-todo" }),
});

export const todoHighlighter: Extension = [
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = todoMatcher.createDeco(view);
      }
      update(u: ViewUpdate) {
        this.decorations = todoMatcher.updateDeco(u, this.decorations);
      }
    },
    { decorations: (v) => v.decorations },
  ),
  EditorView.baseTheme({
    ".cm-todo": {
      fontWeight: "700",
      padding: "0 3px",
      borderRadius: "3px",
      outline: "1px solid currentColor",
      outlineOffset: "-1px",
    },
  }),
];

/** Move o cursor para o próximo TODO (dá a volta no fim do arquivo). */
export function gotoNextTodo(view: EditorView): boolean {
  const text = view.state.doc.toString();
  const head = view.state.selection.main.head;
  const re = new RegExp(TODO_RE.source, "g");
  re.lastIndex = head + 1;
  const match = re.exec(text) ?? ((re.lastIndex = 0), re.exec(text));
  if (!match) return false;
  view.dispatch({
    selection: { anchor: match.index, head: match.index + match[0].length },
    scrollIntoView: true,
  });
  view.focus();
  return true;
}

// ───────────── Prévia de cores ─────────────
class ColorSwatch extends WidgetType {
  constructor(readonly color: string) {
    super();
  }
  override eq(other: ColorSwatch) {
    return other.color === this.color;
  }
  toDOM() {
    const el = document.createElement("span");
    el.className = "cm-color-swatch";
    el.style.backgroundColor = this.color;
    el.setAttribute("aria-hidden", "true");
    return el;
  }
}
const colorMatcher = new MatchDecorator({
  regexp: /#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{3,4})\b|rgba?\([^)\n]{5,40}\)/g,
  decorate: (add, from, _to, match) => {
    add(from, from, Decoration.widget({ widget: new ColorSwatch(match[0]), side: -1 }));
  },
});

export const colorPreview: Extension = [
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = colorMatcher.createDeco(view);
      }
      update(u: ViewUpdate) {
        this.decorations = colorMatcher.updateDeco(u, this.decorations);
      }
    },
    { decorations: (v) => v.decorations },
  ),
  EditorView.baseTheme({
    ".cm-color-swatch": {
      display: "inline-block",
      width: "0.8em",
      height: "0.8em",
      marginRight: "4px",
      verticalAlign: "-0.05em",
      borderRadius: "3px",
      boxShadow: "0 0 0 1px rgba(128,128,128,.5)",
    },
  }),
];

// ───────────── Espaços visíveis ─────────────
export const visibleWhitespace: Extension = [highlightWhitespace(), highlightTrailingWhitespace()];

// ───────────── Vim (carregado sob demanda) ─────────────
export async function loadVim(): Promise<Extension> {
  const { vim } = await import("@replit/codemirror-vim");
  // Precedência máxima: o Vim precisa ver as teclas antes dos outros atalhos.
  return Prec.highest(vim({ status: true }));
}

// ───────────── Prettier: atalho do editor ─────────────
export function prettierKeymap(format: (view: EditorView) => void): Extension {
  return Prec.high(
    keymap.of([
      {
        key: "Shift-Alt-f",
        preventDefault: true,
        run: (view) => {
          format(view);
          return true;
        },
      },
    ]),
  );
}
